"use client";
import React, { useEffect, useState } from "react";
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	TextField,
	Typography,
	useMediaQuery,
	useTheme,
	Box,
} from "@mui/material";
import { MdDeleteForever } from "react-icons/md";
import { FaEdit } from "react-icons/fa";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useGetQueryWithTotal } from "src/hooks/useGetQueryWithTotal";
import { useApiMutation } from "src/hooks/useApiMutation";
import { imgUrl } from "src/Helpers/utils/constants";
import type { EventDate, EventItem, NewEventItem } from "./type/conferenceType";

const ConferenceList = () => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

	const [selectedDate, setSelectedDate] = useState<string | null>(null);
	const [openDelete, setOpenDelete] = useState(false);
	const [openEditDialog, setOpenEditDialog] = useState(false);
	const [selectedId, setSelectedId] = useState<number | null>(null);
	const [editData, setEditData] = useState<EventItem | null>(null);
	const [file, setFile] = useState<File | null>(null);
	const [preview, setPreview] = useState<string | null>(null);
	const [originalEditData, setOriginalEditData] = useState<EventItem | null>(
		null
	);
	const [openAddDialog, setOpenAddDialog] = useState(false);
	const [newEventData, setNewEventData] = useState<NewEventItem>({
		title: "",
		description: "",
		location: "",
		event_date: selectedDate || "",
		start_time: "",
		end_time: "",
		type: "conference",
	});
	const [newFile, setNewFile] = useState<File | null>(null);
	const [newPreview, setNewPreview] = useState<string | null>(null);

	// Fetch dates
	const { data: dateResponse, isLoading: isLoadingDates } =
		useGetQueryWithTotal<EventDate[]>({
			endPoint: "/api/fair/schedule/dates?type=conference",
			queryKeys: ["ConferenceDates"],
		});

	// Fetch events for selected date
	const {
		data: eventResponse,
		isLoading: isLoadingEvents,
		refetch,
	} = useGetQueryWithTotal<{ data: EventItem[]; total: number }>({
		endPoint: selectedDate
			? `/api/fair/schedule?date=${selectedDate}&type=conference`
			: undefined,
		queryKeys: ["ConferenceEvents", selectedDate],
		enabled: !!selectedDate,
	});

	// Default selected date
	useEffect(() => {
		if (!selectedDate && dateResponse?.data?.length) {
			setSelectedDate(dateResponse.data[0].event_date);
		}
	}, [dateResponse, selectedDate]);

	// Mutations
	const { mutate: deleteConference } = useApiMutation<{}, any>({
		method: "DELETE",
		endPoint: "/api/fair/schedule/delete",
		invalidateQueries: ["ConferenceEvents", "ConferenceDates"],
	});

	const { mutate: updateConference } = useApiMutation<FormData, any>({
		method: "PATCH",
		endPoint: "/api/fair/schedule/",
		invalidateQueries: ["ConferenceEvents"],
	});

	const { mutate: addConference } = useApiMutation<FormData, any>({
		method: "POST",
		endPoint: "/api/fair/schedule",
		invalidateQueries: ["ConferenceEvents", "ConferenceDates"],
	});

	// Delete handlers
	const handleDeleteClick = (id: number) => {
		setSelectedId(id);
		setOpenDelete(true);
	};

	const confirmDelete = () => {
		if (!selectedId) return;
		deleteConference(
			{
				dynamicEndpoint: `/api/fair/schedule/delete/${selectedId}?type=conference`,
			},
			{
				onSuccess: () => {
					setOpenDelete(false);
					setSelectedId(null);
					refetch();
				},
				onError: (error: any) => {
					console.error(error);
					alert("Failed to delete event.");
				},
			}
		);
	};

	// Edit handlers
	const handleUpdateClick = (event: EventItem) => {
		setEditData(event);
		setOriginalEditData(event);
		setPreview(event.file ? `${imgUrl}/${event.file}` : null);
		setFile(null);
		setOpenEditDialog(true);
	};

	const handleEditSave = () => {
		if (!editData || !originalEditData) return;

		const formatTime = (t: string) => (t ? t.slice(0, 5) : "");
		const formData = new FormData();

		// Basic fields
		const fields: (keyof EventItem)[] = [
			"title",
			"description",
			"location",
			"event_date",
		];

		fields.forEach((key) => {
			if (editData[key] !== originalEditData[key]) {
				formData.append(key, editData[key] as string);
			}
		});

		const eventDateChanged =
			editData.event_date !== originalEditData.event_date;

		// Time fields
		(["start_time", "end_time"] as const).forEach((key) => {
			const newTime = formatTime(editData[key]);
			const oldTime = formatTime(originalEditData[key]);

			// Always append if event_date changed or time value changed
			if (eventDateChanged || newTime !== oldTime) {
				formData.append(key, newTime);
			}
		});

		if (file) formData.append("file", file);

		if (![...formData.keys()].length) {
			alert("No changes detected.");
			return;
		}

		updateConference(
			{
				dynamicEndpoint: `/api/fair/schedule/${editData.id}?type=${editData.type}`,
				formData,
			} as any,
			{
				onSuccess: () => {
					setOpenEditDialog(false);
					setEditData(null);
					setOriginalEditData(null);
					setFile(null);
					setPreview(null);
					refetch();
				},
				onError: (err) => {
					console.error(err);
				},
			}
		);
	};

	const handleAddSave = () => {
		const formData = new FormData();
		formData.append("title", newEventData.title);
		formData.append("description", newEventData.description);
		formData.append("location", newEventData.location);
		formData.append("event_date", newEventData.event_date);
		formData.append("start_time", newEventData.start_time);
		formData.append("end_time", newEventData.end_time);
		formData.append("type", "conference");
		if (newFile) formData.append("file", newFile);

		addConference({ formData } as any, {
			onSuccess: () => {
				setOpenAddDialog(false);
				setNewEventData({
					title: "",
					description: "",
					location: "",
					event_date: selectedDate || "",
					start_time: "",
					end_time: "",
					file: "",
					type: "",
				});
				setNewFile(null);
				setNewPreview(null);
				refetch();
			},
			onError: (err) => {
				console.error(err);
				alert("Failed to add event.");
			},
		});
	};

	const events: EventItem[] = Array.isArray(eventResponse?.data)
		? eventResponse.data
		: [];

	return (
		<LocalizationProvider dateAdapter={AdapterDayjs}>
			<Box p={2}>
				<div
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						width: "100%",
					}}
				>
					<Typography variant="h5" gutterBottom>
						Conference Schedule
					</Typography>
					<Button
						variant="contained"
						onClick={() => setOpenAddDialog(true)}
					>
						Add Conference
					</Button>
				</div>
				{/* Date buttons */}
				{isLoadingDates ? (
					<p>Loading dates...</p>
				) : (
					<Box
						sx={{
							display: "flex",
							gap: 1,
							my: 2,
							flexWrap: "nowrap",
							overflowX: "auto",
							"&::-webkit-scrollbar": { display: "none" },
							"-ms-overflow-style": "none",
							"scrollbar-width": "none",
							px: 1,
						}}
					>
						{dateResponse?.data?.map((item) => (
							<Button
								key={item.event_date}
								variant={
									item.event_date === selectedDate
										? "contained"
										: "outlined"
								}
								onClick={() => setSelectedDate(item.event_date)}
								sx={{ flex: "0 0 auto" }}
							>
								{item.event_date}
							</Button>
						))}
					</Box>
				)}

				{/* Events */}
				{!selectedDate ? (
					<p>Select a date to view events.</p>
				) : isLoadingEvents ? (
					<p>Loading events...</p>
				) : events.length > 0 ? (
					events.map((event) => (
						<Box
							key={event.id}
							display="flex"
							flexDirection={isMobile ? "column" : "row"}
							alignItems={isMobile ? "center" : "flex-start"}
							gap={2}
							p={2}
							mb={2}
							border="1px solid #ddd"
							borderRadius={2}
							boxShadow="0 2px 5px rgba(0,0,0,0.05)"
							bgcolor="#fff"
						>
							{event.file && (
								<img
									src={`${imgUrl}/${event.file}`}
									alt={event.title}
									style={{
										width: isMobile ? "100%" : "360px",
										height: isMobile ? "200px" : "240px",
										objectFit: "cover",
										borderRadius: 8,
									}}
								/>
							)}
							<Box flex={1}>
								<Typography variant="h6">
									<b>{event.title}</b>
								</Typography>
								<Box
									sx={{
										maxHeight: 160, // set desired height
										overflowY: "auto",
										whiteSpace: "pre-line",
										mb: 1, // optional margin below
										pr: 1, // padding right so scrollbar doesn't overlay text
									}}
								>
									<Typography variant="body2">
										{event.description}
									</Typography>
								</Box>

								<Box
									display="flex"
									flexDirection={isMobile ? "column" : "row"}
									justifyContent="space-between"
									alignItems="center"
									mt={1}
								>
									<Box
										style={{ display: "flex", gap: "8px" }}
									>
										<Typography>
											<b>Location:</b> {event.location}
										</Typography>
										<Typography>
											<b>Time:</b> {event.start_time} -{" "}
											{event.end_time}
										</Typography>
									</Box>

									<Box>
										<Button
											onClick={() =>
												handleUpdateClick(event)
											}
											color="success"
											startIcon={<FaEdit />}
										>
											Edit
										</Button>
										<Button
											onClick={() =>
												handleDeleteClick(event.id)
											}
											color="error"
											startIcon={<MdDeleteForever />}
										>
											Delete
										</Button>
									</Box>
								</Box>
							</Box>
						</Box>
					))
				) : (
					<p>No events found for {selectedDate}</p>
				)}

				{/* Delete Dialog */}
				<Dialog open={openDelete} onClose={() => setOpenDelete(false)}>
					<DialogTitle>Confirm Delete</DialogTitle>
					<DialogContent>
						<DialogContentText>
							Are you sure you want to delete this event? This
							action cannot be undone.
						</DialogContentText>
					</DialogContent>
					<DialogActions>
						<Button onClick={() => setOpenDelete(false)}>
							Cancel
						</Button>
						<Button
							onClick={confirmDelete}
							color="error"
							variant="contained"
						>
							Delete
						</Button>
					</DialogActions>
				</Dialog>

				{/* Edit Dialog */}
				<Dialog
					open={openEditDialog}
					onClose={() => setOpenEditDialog(false)}
					fullWidth
					maxWidth="sm"
				>
					<DialogTitle>Edit Conference Event</DialogTitle>
					<DialogContent>
						<Box
							display="flex"
							flexDirection="column"
							gap={2}
							mt={1}
						>
							<TextField
								label="Title"
								value={editData?.title || ""}
								onChange={(e) =>
									setEditData({
										...editData!,
										title: e.target.value,
									})
								}
								fullWidth
							/>
							<TextField
								label="Description"
								multiline
								rows={4}
								value={editData?.description || ""}
								onChange={(e) =>
									setEditData({
										...editData!,
										description: e.target.value,
									})
								}
								fullWidth
							/>
							<TextField
								label="Location"
								value={editData?.location || ""}
								onChange={(e) =>
									setEditData({
										...editData!,
										location: e.target.value,
									})
								}
								fullWidth
							/>
							<DatePicker
								label="Event Date"
								value={
									editData?.event_date
										? dayjs(editData.event_date)
										: null
								}
								onChange={(newValue) =>
									setEditData({
										...editData!,
										event_date: newValue
											? newValue.format("YYYY-MM-DD")
											: "",
									})
								}
								slotProps={{ textField: { fullWidth: true } }}
							/>
							<Box display="flex" gap={2} flexWrap="wrap">
								<TextField
									label="Start Time"
									type="time"
									value={editData?.start_time || ""}
									onChange={(e) =>
										setEditData({
											...editData!,
											start_time: e.target.value,
										})
									}
									InputLabelProps={{ shrink: true }}
								/>
								<TextField
									label="End Time"
									type="time"
									value={editData?.end_time || ""}
									onChange={(e) =>
										setEditData({
											...editData!,
											end_time: e.target.value,
										})
									}
									InputLabelProps={{ shrink: true }}
								/>
							</Box>
							{preview && (
								<img
									src={preview}
									alt="preview"
									style={{
										width: "100%",
										maxHeight: 200,
										objectFit: "cover",
										borderRadius: 8,
									}}
								/>
							)}
							<Button variant="outlined" component="label">
								Change Image
								<input
									type="file"
									hidden
									accept="image/*"
									onChange={(e) => {
										const newFile =
											e.target.files?.[0] || null;
										setFile(newFile);
										if (newFile) {
											const reader = new FileReader();
											reader.onload = () =>
												setPreview(
													reader.result as string
												);
											reader.readAsDataURL(newFile);
										}
									}}
								/>
							</Button>
						</Box>
					</DialogContent>
					<DialogActions>
						<Button onClick={() => setOpenEditDialog(false)}>
							Cancel
						</Button>
						<Button
							onClick={handleEditSave}
							variant="contained"
							color="primary"
						>
							Save Changes
						</Button>
					</DialogActions>
				</Dialog>
				{/* Add Dialog */}
				<Dialog
					open={openAddDialog}
					onClose={() => setOpenAddDialog(false)}
					fullWidth
					maxWidth="sm"
				>
					<DialogTitle>Add New Conference Event</DialogTitle>
					<DialogContent>
						<Box
							display="flex"
							flexDirection="column"
							gap={2}
							mt={1}
						>
							<TextField
								label="Title"
								value={newEventData.title}
								onChange={(e) =>
									setNewEventData({
										...newEventData,
										title: e.target.value,
									})
								}
								fullWidth
							/>
							<TextField
								label="Description"
								multiline
								rows={4}
								value={newEventData.description}
								onChange={(e) =>
									setNewEventData({
										...newEventData,
										description: e.target.value,
									})
								}
								fullWidth
							/>
							<TextField
								label="Location"
								value={newEventData.location}
								onChange={(e) =>
									setNewEventData({
										...newEventData,
										location: e.target.value,
									})
								}
								fullWidth
							/>
							<DatePicker
								label="Event Date"
								value={dayjs(newEventData.event_date)}
								onChange={(newValue) =>
									setNewEventData({
										...newEventData,
										event_date:
											newValue?.format("YYYY-MM-DD") ||
											"",
									})
								}
								slotProps={{ textField: { fullWidth: true } }}
							/>
							<Box display="flex" gap={2} flexWrap="wrap">
								<TextField
									label="Start Time"
									type="time"
									value={newEventData.start_time}
									onChange={(e) =>
										setNewEventData({
											...newEventData,
											start_time: e.target.value,
										})
									}
									InputLabelProps={{ shrink: true }}
								/>
								<TextField
									label="End Time"
									type="time"
									value={newEventData.end_time}
									onChange={(e) =>
										setNewEventData({
											...newEventData,
											end_time: e.target.value,
										})
									}
									InputLabelProps={{ shrink: true }}
								/>
							</Box>
							{newPreview && (
								<img
									src={newPreview}
									alt="preview"
									style={{
										width: "100%",
										maxHeight: 200,
										objectFit: "cover",
										borderRadius: 8,
									}}
								/>
							)}
							<Button variant="outlined" component="label">
								Upload Image
								<input
									type="file"
									hidden
									accept="image/*"
									onChange={(e) => {
										const file =
											e.target.files?.[0] || null;
										setNewFile(file);
										if (file) {
											const reader = new FileReader();
											reader.onload = () =>
												setNewPreview(
													reader.result as string
												);
											reader.readAsDataURL(file);
										}
									}}
								/>
							</Button>
						</Box>
					</DialogContent>
					<DialogActions>
						<Button onClick={() => setOpenAddDialog(false)}>
							Cancel
						</Button>
						<Button
							onClick={handleAddSave}
							variant="contained"
							color="primary"
						>
							Add Event
						</Button>
					</DialogActions>
				</Dialog>
			</Box>
		</LocalizationProvider>
	);
};

export default ConferenceList;
